import { useRef, useState } from 'react';
import {
  AccountBalance,
  AccountDetailsMap,
  AccountPermissionsMap,
  PaginationMetaData,
  PARTNER_ACCOUNT_STATUSES,
  PartnerAccountStatus,
  PartnerAccountsRequest as ApiPartnerAccountsRequest,
  dataTransformers,
} from 'vopay';
import { api } from '@/services/api.config';

/**
 * What an account picker asks for in one go. The listing is unbounded without a `Limit` (see
 * `toRequestParams`), which is right for an export but not for a dropdown that refetches per
 * keystroke — those pass this and let the server's search find the rest.
 */
export const ACCOUNT_PICKER_PAGE_SIZE = 20;

/** The filter's vocabulary: an account status, plus the two "no filter" values the UI sends. */
export type PartnerAccountStatusFilter = PartnerAccountStatus | '' | 'all';

export type PartnerAccountsRequest = Omit<ApiPartnerAccountsRequest, 'VopayAccountStatus'> & {
  VopayAccountStatus?: PartnerAccountStatusFilter;
};

function isPartnerAccountStatus(value: string): value is PartnerAccountStatus {
  return PARTNER_ACCOUNT_STATUSES.some((status) => status === value);
}

/**
 * Narrows a raw query-string value to the filter's vocabulary. The URL is user-editable and holds
 * `all` for "no filter", neither of which is a `PartnerAccountStatus` — so a component that reads it
 * has to narrow rather than assert, or it types a lie. Anything unrecognised means no filter.
 */
export function toPartnerAccountStatusFilter(value: string | null | undefined): PartnerAccountStatusFilter {
  if (!value) return '';

  return value === 'all' || isPartnerAccountStatus(value) ? value : '';
}

export type FetchPartnerAccountsResponse = {
  accountDetails: AccountDetailsMap;
  balances: AccountBalance[];
  permissions: AccountPermissionsMap;
  /** Present only on an export response; the listing and the file are mutually exclusive. */
  CSVFile?: string;
  /** Absent on an export response, and when the listing's pagination metadata is empty. */
  PaginationMetaData?: PaginationMetaData;
  /** Partner-level program terms; ride along on every page of the listing. Null when not applicable. */
  RevShare: string | null;
  MinimumProgramFee: string | null;
};

/**
 * `'all'` and `''` are how the filter UI says "no filter"; the request wants them empty or absent,
 * so they are normalized here rather than at each call site.
 */
function toRequestParams({ ExportCSV, Country, Currency, VopayAccountStatus, ...rest }: PartnerAccountsRequest) {
  const isFiltered = (value?: string) => value !== undefined && value !== '' && value !== 'all';

  // A type guard rather than the same check inline, so narrowing out '' / 'all' leaves the request's
  // own `PartnerAccountStatus` without a cast.
  const isAccountStatus = (value?: PartnerAccountStatusFilter): value is PartnerAccountStatus => isFiltered(value);

  return {
    ...rest,
    ExportCSV,
    ShowToken: true,
    // No Limit default: earthnode paginates only when one is sent, so an argument-less call means
    // "every account" — which is what the breadcrumb lookup and the ticket form's account picker
    // need. Paginating callers state their page size.
    //
    // The export is the whole filtered set by definition, so pagination is stripped rather than
    // merely left undefaulted: `...rest` would otherwise let a caller's page size truncate the file.
    ...(ExportCSV ? { Limit: undefined, Offset: undefined } : { Offset: rest.Offset || 0 }),
    Country: isFiltered(Country) ? Country : '',
    Currency: isFiltered(Currency) ? Currency : '',
    VopayAccountStatus: isAccountStatus(VopayAccountStatus) ? VopayAccountStatus : undefined,
  };
}

/**
 * The partner's book of accounts — the listing, and the CSV export of the same query. `bearer` scopes
 * the call to another account's token, which the partner-details Accounts tab uses.
 */
export default function useFetchPartnerAccounts({ bearer }: { bearer?: string } = {}) {
  const [fetching, setFetching] = useState(false);
  const [response, setResponse] = useState<FetchPartnerAccountsResponse>();

  const latestRequestId = useRef(0);

  async function fetchPartnerAccounts(params: PartnerAccountsRequest = {}) {
    latestRequestId.current += 1;
    const requestId = latestRequestId.current;
    const isLatest = () => latestRequestId.current === requestId;

    try {
      setFetching(true);

      const res = await api.fetchPartnerAccounts(toRequestParams(params), undefined, bearer);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      const { toAccountDetails, toBalances, toPermissions } = dataTransformers.fromAccounts(res.Accounts);

      const mapped: FetchPartnerAccountsResponse = {
        accountDetails: toAccountDetails,
        balances: toBalances,
        permissions: toPermissions,
        CSVFile: res.CSVFile,
        PaginationMetaData: res.PaginationMetaData,
        RevShare: res.RevShare,
        MinimumProgramFee: res.MinimumProgramFee,
      };

      // Only the newest request writes the state: a picker refetches per keystroke and nothing
      // cancels the request already in flight, so a slower earlier response would otherwise
      // overwrite the results for what the user actually typed.
      if (isLatest()) setResponse(mapped);

      return mapped;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      if (isLatest()) setFetching(false);
    }
  }

  function reset() {
    setResponse(undefined);
  }

  return { response, fetching, fetchPartnerAccounts, reset };
}
